import type { CSSProperties } from 'react';
import { COLOURS } from '../../../../src/design/presentation-tokens.mjs';
import type { NativePresentation } from '../models.ts';

// ART's canonical per-node presentation, as CSS. An absent property is the renderer
// default (presentation/defaults.mjs) and produces no style at all; so removing an
// override (Undo) returns the exact default. Token keys are admitted by DES1's registry:
// an unknown key is dropped, never guessed. Only the declared channels have a surface
// here: fill and stroke belong to charts and shapes, and no geometry is invented.
//
// Where the node sits decides how a token resolves. A document or report sits on paper,
// which is always light (screen and print alike), so a token is its LIGHT value; a
// spreadsheet cell follows the theme, so it is var(--pc-<key>).
export type Surface = 'paper' | 'themed';

const COLOUR_PROPERTY = {
  text: 'color',
  background: 'backgroundColor',
  border: 'borderColor',
} as const;

const resolve = (key: string, surface: Surface): string | null => {
  if (!Object.hasOwn(COLOURS, key)) return null;
  const token = COLOURS[key];
  return surface === 'paper' ? token.light : `var(--pc-${token.cssVar})`;
};

export function presentationStyle(
  presentation: NativePresentation | undefined,
  surface: Surface,
): CSSProperties | undefined {
  if (!presentation) return undefined;
  const style: Record<string, string> = {};
  for (const [channel, key] of Object.entries(presentation.colours ?? {})) {
    if (!Object.hasOwn(COLOUR_PROPERTY, channel)) continue;
    const value = resolve(key, surface);
    if (value) style[COLOUR_PROPERTY[channel as keyof typeof COLOUR_PROPERTY]] = value;
  }
  // Spacing is exact CSS px, already admitted 0–256 by ART's schema; the side name is
  // the CSS property name, so it is passed through the same own-key rule.
  for (const [side, px] of Object.entries(presentation.spacing ?? {})) {
    if (typeof px === 'number') style[side] = `${px}px`;
  }
  return Object.keys(style).length > 0 ? (style as CSSProperties) : undefined;
}
